import { useState, useCallback, useEffect, useMemo } from 'react';
import { ArrowDown } from '@phosphor-icons/react';
import { PaginationWrapper } from '../../Utility/PaginationWrapper';
import { PaginationListType } from '../../../Types/PaginationParams';
import SearchFilter from '../../Utility/SearchFilter';
import useSearchFilter from '../../../Hooks/useSearchFilter';
import ConfigSelectorItem from './ConfigSelectorItem';

interface BaseConfig {
    identifier: string;
    title: string;
}

interface ConfigItem {
    identifier: string;
    title: string;
    description: string;
    config?: BaseConfig;
}

interface ConfigSelectorSectionProps {
    config: BaseConfig;
    selectedItems: { identifier: string; identifierOfConfig: string }[];
    onToggleItem: (itemId: string, identifierOfConfig: string) => void;
    i18n: any;
    itemsFunction: (
        configIdentifier: string,
        page: number,
        itemsPerPage: number,
        query?: string | null,
    ) => Promise<{ data: { data: ConfigItem[]; total: number } }>;
    getListType: (configIdentifier: string) => PaginationListType;
    setConfigsWithEndpoints?: (count: number) => void;
}

function ConfigSelectorSection({
    config,
    selectedItems,
    onToggleItem,
    i18n,
    itemsFunction,
    getListType,
}: ConfigSelectorSectionProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [selectedCountForThisConfig, setSelectedCountForThisConfig] = useState<number>(0);
    const [knownEndpointIds, setKnownEndpointIds] = useState<Set<string>>(new Set());
    const { query, localQuery, searchActions } = useSearchFilter({ initialQuery: null });

    const toggleExpansion = () => {
        setIsExpanded(!isExpanded);
    };

    const wrappedItemsFunction = useCallback(
        async (
            page: number,
            itemsPerPage: number,
            optionalParams?: Record<string, string>,
            queryParam?: string | null,
        ) => {
            const response = await itemsFunction(config.identifier, page, itemsPerPage, queryParam);

            if (response.data.data) {
                setKnownEndpointIds((prev) => {
                    const newSet = new Set(prev);
                    response.data.data.forEach((item: ConfigItem) => {
                        newSet.add(item.identifier);
                    });
                    return newSet;
                });
            }

            return response;
        },
        [itemsFunction, config.identifier],
    );

    useEffect(() => {
        const initLoad = async () => {
            const response = await wrappedItemsFunction(1, 10, {}, null);
            if (response.data.total !== undefined) {
                setTotalItems(response.data.total);
            }
        };
        initLoad();
    }, [wrappedItemsFunction]);

    const [hasLoadedAllIds, setHasLoadedAllIds] = useState(false);

    useEffect(() => {
        if (!isExpanded || totalItems === 0 || hasLoadedAllIds) return;

        if (knownEndpointIds.size >= totalItems) {
            setHasLoadedAllIds(true);
            return;
        }

        const loadAllEndpointIds = async () => {
            await wrappedItemsFunction(1, totalItems, {}, null);
            setHasLoadedAllIds(true);
        };

        loadAllEndpointIds();
    }, [isExpanded, totalItems, hasLoadedAllIds, knownEndpointIds.size, wrappedItemsFunction]);

    useEffect(() => {
        setSelectedCountForThisConfig(
            selectedItems.filter((selectedItem) => selectedItem.identifierOfConfig === config.identifier).length,
        );
    }, [selectedItems, config.identifier]);

    if (totalItems === 0) {
        return null;
    }

    return (
        <div>
            <button onClick={toggleExpansion} className="w-full flex items-center py-4 text-left">
                <span className="mr-3 text-gray-500 flex justify-center">
                    <button
                        className="rounded-full h-[25px] md:h-[30px] w-[25px] md:w-[30px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground hover:bg-chatInput-sendButtonBackground-hover border-none flex items-center justify-center"
                        title={i18n.translate({
                            id: 'selector.showHidePrompts',
                            sourceName: 'PromptFunction',
                        })}
                    >
                        <ArrowDown
                            size={20}
                            style={{
                                transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease',
                            }}
                        />
                    </button>
                </span>
                <span className="text-layout-textcolor flex-1">
                    <span className="font-bold">{config.title}</span>
                    <span className="text-gray-500 text-sm pl-2">
                        ({selectedCountForThisConfig}/{totalItems})
                    </span>
                </span>
            </button>

            <div className={`${isExpanded ? 'h-fit ' : 'h-0'} overflow-hidden`}>
                <div className="space-y-2">
                    <div className="pt-4 space-y-3">
                        <SearchFilter
                            value={localQuery}
                            onChange={searchActions.setLocalQuery}
                            onApply={searchActions.applyQuery}
                            onClear={searchActions.clearQuery}
                            className="mb-4 max-w-full"
                        />
                    </div>
                    <div className="pb-4">
                        <PaginationWrapper
                            listType={getListType(config.identifier)}
                            itemsFunction={wrappedItemsFunction}
                            itemsPerPage={10}
                            query={query || null}
                            renderItem={(item: ConfigItem) => (
                                <ConfigSelectorItem
                                    key={item.identifier}
                                    item={item}
                                    isSelected={
                                        selectedItems.some(
                                            (selectedItem) => selectedItem.identifier === item.identifier,
                                        ) || false
                                    }
                                    onToggle={(itemId) => onToggleItem(itemId, config.identifier)}
                                />
                            )}
                            className="space-y-3"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfigSelectorSection;
